import { useCallback, useState, useEffect } from 'react';
import { useDebounce } from '@clover-util/use-debounce';

type CounterState = Readonly<{ value: number; min: number; max: number; isAtMax: boolean; isAtMin: boolean; step: number }>;
type Increment = () => void;
type Decrement = () => void;
type SetValue = (value: number | React.FormEvent<HTMLInputElement> | React.ChangeEvent<HTMLInputElement>) => void;
type CounterResult = [CounterState, Increment, Decrement, SetValue] & { state: CounterState; increment: Increment; decrement: Decrement; setValue: SetValue };

const WAIT = 120;

function createValueUpdater(updater: (value: number, step: number) => number): (state: CounterState) => CounterState {
  return state => {
    const value = Math.max(state.min, Math.min(state.max, updater(state.value, state.step)));

    if (value === state.value) return state;

    return { ...state, value, isAtMax: value === state.max, isAtMin: value === state.min };
  };
}

export function useCounter(value: number, min = -(Number.MAX_SAFE_INTEGER), max = Number.MAX_SAFE_INTEGER, step = 1): CounterResult {
  const [state, setState] = useState({ min, max, step, value, isAtMax: value === max, isAtMin: value === min });

  useEffect(() => setState(state => state.min === min ? state : ({ ...state, min, isAtMin: state.value === min })), [min]);
  useEffect(() => setState(state => state.max === max ? state : ({ ...state, max, isAtMax: state.value === max })), [max]);
  useEffect(() => setState(state => state.step === step ? state : ({ ...state, step })), [step]);

  const decrement: Decrement = useCallback(() => setState(createValueUpdater((value, step) => value - step)), []);
  const increment: Increment = useCallback(() => setState(createValueUpdater((value, step) => value + step)), []);
  const update: SetValue = useDebounce(input => {
    const value = typeof input === 'number' ? input : (input.target as EventTarget & HTMLInputElement)?.valueAsNumber;

    // ignore user input for delete or bad
    if (isNaN(value)) return;

    setState(previousState => {
      const state = createValueUpdater(() => value)(previousState);

      if (typeof input !== 'number') {
        // eslint-disable-next-line functional/immutable-data
        (input.target as EventTarget & HTMLInputElement).valueAsNumber = state.value;
      }

      return state;
    });
  }, WAIT, []);

  const setValue: SetValue = useCallback(input => {
    if (typeof input !== 'number') {
      input.persist();
    }

    update(input);
  }, [update]);

  return Object.assign([state, increment, decrement, setValue], { state, increment, decrement, setValue }) as CounterResult;
}
